import React, { createContext } from 'react'
import Config from '../../config'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IAdminUser from '../../interfaces/IAdminUser'
import { loadStorage, saveStorage, clearStorage } from '../../services/storage'
import { SessionRequest, SessionInfoRequest, SessionRegisterRequest } from '../../services/api/interfaces/ApiRequest'
import { setToken, sessionRequest, sessionRegister, sessionInfo } from '../../services/api'

// That's our local storage key
export const STORAGE_KEY = '@adopets/admin-auth'

interface AuthContextState extends BaseContextState {
  loggedIn: boolean,
  accessKey: string,
  accessToken: string,
  adminUser: IAdminUser | null
}

interface AuthContextData extends AuthContextState {
  resetErrors: Function,
  requestSession: Function,
  requestSessionRegister: Function,
  requestSessionInfo: Function,
  requestSessionUnRegister: Function
}

// Create context
const { Provider, Consumer } = createContext({} as AuthContextData)

// Export consumer
export const AuthConsumer = Consumer

export class AuthProvider extends BaseContextProvider {
  state: AuthContextState = {
    loggedIn: false,
    accessKey: '',
    accessToken: '',
    adminUser: null
  }

  constructor(props: any) {
    super(props)

    const store = loadStorage(STORAGE_KEY)

    // We do have stored user data?
    if (store) {
      this.state = {
        ...this.state,
        ...store,
        loggedIn: true
      }
    }
  }

  componentDidMount() {
    const { accessToken } = this.state

    if (accessToken && accessToken.length > 0) {
      setToken(accessToken)
    }
  }

  /**
   * Make api request for session
   * 
   */
  requestSession = () => {
    this.setState({
      fetching: true,
      success: false,
      accessKey: '',
      adminUser: null,
      error: null
    }, async () => {
      const payload: SessionRequest = {
        system_api_key: Config.apiKey
      }
      const response = await sessionRequest(payload)

      // Process our response
      this.processResponse(response, {
        mappings: [
          { origin: 'access_key', destination: 'accessKey' }
        ]
      })
    })
  }

  /**
   * Make api request for session registration
   * 
   * @param {SessionRegisterRequest} payload 
   */
  requestSessionRegister = (payload: SessionRegisterRequest) => {
    this.setState({
      fetching: true,
      success: false,
      adminUser: null,
      error: null
    }, async () => {
      setToken(this.state.accessKey)
      const response = await sessionRegister(payload)

      // Process our response
      this.processResponse(response, {
        mappings: [
          { origin: 'access_key', destination: 'accessToken' },
          { origin: 'admin_user', destination: 'adminUser' }
        ]
      })

      // Everything is fine
      if (this.state.success && this.state.adminUser) {
        // Update request header token
        setToken(this.state.accessToken)

        this.setState({
          loggedIn: true,
          accessKey: ''
        })

        saveStorage(STORAGE_KEY, {
          accessToken: this.state.accessToken,
          adminUser: this.state.adminUser
        })
      }
    })
  }

  /**
   * Make api request for session info
   * 
   * @param {SessionInfoRequest} payload 
   */
  requestSessionInfo = (payload: SessionInfoRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      setToken(this.state.accessToken)
      const response = await sessionInfo(payload)

      // Process our response
      this.processResponse(response, {
        mappings: [
          { origin: 'admin_user', destination: 'adminUser' }
        ]
      })
      // Everything is fine
      if (this.state.adminUser && response?.data?.prefix !== "5xx") {
        saveStorage(STORAGE_KEY, {
          accessToken: this.state.accessToken,
          adminUser: this.state.adminUser
        })
      }
    })
  }

  /**
   * Just logout
   */
  requestSessionUnRegister = () => {
    this.setState({
      fetching: false,
      success: true,
      error: null,
      loggedIn: false,
      accessToken: '',
      adminUser: null
    }, () => {
      clearStorage(STORAGE_KEY)
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestSession: this.requestSession,
          requestSessionRegister: this.requestSessionRegister,
          requestSessionInfo: this.requestSessionInfo,
          requestSessionUnRegister: this.requestSessionUnRegister
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
